import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, ScanLine, Loader2, CheckCircle2, FileText, Files, Layers,
  CreditCard, HelpCircle, UploadCloud, IdCard, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, describeError } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import { validateDocFile, ACCEPTED, startSplitUpload } from "@/lib/noteClaim";
import NoteClaimFlow from "@/components/NoteClaimFlow";
import MultiClaimFlow from "@/components/claim/MultiClaimFlow";
import SplitBatchTracker from "@/components/claim/SplitBatchTracker";
import EobEraFlow from "@/components/EobEraFlow";
import { uploadEob } from "@/lib/eobEraV2";
import "./lens-upload.css";

// Every mode the "Scan with Lens" chooser can offer, keyed to the live flow it runs.
const MODES = {
  single: {
    icon: FileText, title: "Single document → one claim",
    desc: "Upload one superbill, note or PDF — revQ reads it and codes an 837P claim.",
    dropTitle: "Upload one document, or browse",
    chips: ["Superbill", "Encounter note", "PDF", "DFT", "Photo"],
    sample: "Scan a sample document", live: true, multi: false, card: true,
  },
  batch: {
    icon: Files, title: "Batch → many claims",
    desc: "Drop several documents — each one becomes its own claim.",
    dropTitle: "Drop many files, or browse",
    chips: ["Superbills", "Notes", "PDFs", "DFTs", "Photos"],
    sample: "Simulate a batch", live: true, multi: true, card: false,
  },
  visit: {
    icon: Layers, title: "Multiple documents for one visit",
    desc: "Group several documents that all belong to a single visit — together they become one claim.",
    dropTitle: "Add documents for one visit",
    chips: ["Encounter note", "Labs", "Radiology", "Chart", "Insurance/ID card"],
    sample: "Try a sample visit packet", live: true, multi: true, card: true,
  },
  era: {
    icon: CreditCard, title: "EOB → ERA",
    desc: "A paper EOB becomes an ERA — auto-posted to the right claims.",
    dropTitle: "Drop or photograph an EOB",
    chips: ["EOB", "Remittance", "PDF", "Photo"],
    sample: "Try a sample EOB", live: true, multi: true, card: false,
  },
  shawl: {
    icon: HelpCircle, title: "Denied EOB → Ask revQ (SHAWL)",
    desc: "Turn a denied EOB into a next-best-action recovery plan.",
    dropTitle: "Drop a denied EOB",
    chips: ["Denied EOB", "CARC / RARC", "PDF", "Photo"],
    sample: "Try a sample denial", live: false, multi: false, card: false,
  },
};

const KIND = {
  note: { label: "Note", modes: ["single", "batch", "visit"] },
  superbill: { label: "Superbill", modes: ["single", "batch", "visit"] },
  pdf: { label: "PDF", modes: ["single", "batch", "visit", "era"] },
  remittance: { label: "EOB", modes: ["era"] },
};

// Grouping segment: short label, the one-line description under it, and the file-type sentence.
const GROUP = {
  single: { seg: "One claim", desc: "One document becomes one claim.", types: "Superbill · encounter note · PDF · DFT · photo" },
  batch: { seg: "Multiple claims", desc: "Several documents — each becomes its own claim.", types: "Superbill · encounter note · PDF · DFT · photo" },
  visit: { seg: "One visit", desc: "Several documents for one visit — together, one claim.", types: "Superbill · encounter note · lab result · photo" },
};

export default function LensUploadModal({ open, kind = "note", onClose, onDone, minimized = false, onMinimize, onProgress, onWatchClaim, onBackgroundJob }) {
  const cfg = KIND[kind] || KIND.note;
  const modeKeys = cfg.modes;

  const { selectedClientId } = useClient();
  const [allProviders, setProviders] = useState([]);
  const [addedNpis, setAddedNpis] = useState([]);
  // only the NPIs linked to the sidebar's client (a provider added here stays visible)
  const providers = allProviders.filter((p) => String(p.client_id ?? p.client_4digit_id ?? "") === String(selectedClientId ?? "") || addedNpis.includes(p.npi_number));
  const [loadingP, setLoadingP] = useState(true);
  const [npi, setNpi] = useState("");
  const [addNpi, setAddNpi] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [showAddNpi, setShowAddNpi] = useState(false);
  const [queuedJob, setQueuedJob] = useState(null);
  const [firstClientId, setFirstClientId] = useState(null);
  const [staged, setStaged] = useState(null);      // files picked but not yet committed
  const [enqueuing, setEnqueuing] = useState(false);
  const [committing, setCommitting] = useState(false); // submit lock — set the instant commit fires
  const [dupNotice, setDupNotice] = useState(null);    // { noteId, filename, clientId }
  const [fp, setFp] = useState(null);                  // fingerprint of the committed set
  const committingRef = useRef(false);
  const navigate = useNavigate();

  const [mode, setMode] = useState(modeKeys[0]);
  const [files, setFiles] = useState(null);        // single / batch / visit
  const [eraFile, setEraFile] = useState(null);    // era
  const [eraViewOpen, setEraViewOpen] = useState(false);
  const [batch, setBatch] = useState(null);        // smart split-upload batch { id, status }
  const [wide, setWide] = useState(false);
  const [drag, setDrag] = useState(false);
  const [multiDone, setMultiDone] = useState(0);
  const [singleSettled, setSingleSettled] = useState(false);
  const inputRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFiles(null); setEraFile(null); setBatch(null); setWide(false); setAddNpi(""); setMode(modeKeys[0]); setEraViewOpen(false); setSingleSettled(false); setMultiDone(0); setShowAddNpi(false); setQueuedJob(null); setStaged(null); setDupNotice(null); setFp(null); setCommitting(false); committingRef.current = false;
    let cancelled = false;
    setLoadingP(true);
    api.get("/api/edi/note-to-claim/providers")
      .then((r) => { if (!cancelled) { const p = r.data?.providers || []; setProviders(p); setAddedNpis([]); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingP(false); });
    api.get("/api/edi/note-to-claim/clients").then((r) => {
      if (!cancelled) setFirstClientId(r.data?.clients?.[0]?.client_id || r.data?.clients?.[0]?.id || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep the chosen NPI inside the selected client's providers
  useEffect(() => {
    if (!providers.some((p) => p.npi_number === npi)) setNpi(providers[0]?.npi_number || "");
  }, [providers.map((p) => p.npi_number).join(","), selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !files && !eraFile) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, files, eraFile, onClose]);

  // Report processing progress to the page so it can show a "X of Y processed"
  // dock pill while the modal is minimized.
  useEffect(() => {
    if (!open) return;
    const active = !!(files || eraFile || batch);
    if (!active) { onProgress?.(null); return; }
    const isBatch = mode === "batch" && Array.isArray(files) && files.length > 1;
    const total = eraFile ? 1 : (isBatch ? files.length : 1);
    const done = eraFile ? (eraViewOpen ? 1 : 0) : (isBatch ? multiDone : (singleSettled ? 1 : 0));
    onProgress?.({ done, total, active });
  }, [open, files, eraFile, mode, multiDone, singleSettled, wide, eraViewOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const m = MODES[mode];
  // Claim uploads use ONE smart path (auto-split by patient + DOS); no patient pre-selection.
  const isSmart = ["single", "batch", "visit"].includes(mode);
  const inFlow = !!(files || eraFile || batch);
  const claimModes = modeKeys.filter((k) => ["single", "batch", "visit"].includes(k));
  const hasEra = modeKeys.includes("era");
  const hasShawl = modeKeys.includes("shawl");
  const what = ["single", "batch", "visit"].includes(mode) ? "claim" : "remittance";
  const setWhat = (w) => { setStaged(null); if (w === "claim") setMode(claimModes[0] || "single"); else setMode("era"); };
  // Only offer the claim/remittance chooser when BOTH exist; single-purpose opens skip it.
  const showWhatChooser = claimModes.length > 0 && hasEra;
  // Billing provider is a claim concept — hidden for remittance uploads (payee is mapped on the check).
  const showProvider = what === "claim";
  const canStart = what === "remittance" ? true : !!npi;
  const closeModal = () => { if (queuedJob) onBackgroundJob?.(queuedJob); onClose?.(); };
  const handleWatch = (job) => { onWatchClaim?.(job); onClose?.(); };
  const handleBackground = (job) => { onBackgroundJob?.(job || queuedJob); onClose?.(); };
  const stagedN = staged?.length || 0;
  const primaryLabel = !staged
    ? (what === "remittance" ? "Read remittances" : "Split & code documents")
    : what === "remittance" ? `Read ${stagedN} remittance${stagedN > 1 ? "s" : ""}`
      : `Split & code ${stagedN} document${stagedN === 1 ? "" : "s"}`;

  const verify = async () => {
    const clean = addNpi.replace(/\D/g, "");
    if (clean.length !== 10) { toast.error("Enter a valid 10-digit NPI."); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/api/edi/note-to-claim/providers/lookup", { npi_number: clean });
      if (!data?.provider_name) { toast.error("Couldn't confirm that NPI."); return; }
      let saved = data;
      try {
        const { data: s } = await api.post("/api/edi/note-to-claim/providers", { npi_number: clean });
        if (s?.provider) saved = s.provider;
      } catch { /* non-fatal */ }
      setProviders((prev) => prev.some((p) => p.npi_number === clean) ? prev : [...prev, saved]);
      setAddedNpis((a) => (a.includes(clean) ? a : [...a, clean]));
      setNpi(clean); setAddNpi("");
      toast.success("Provider ready", { description: `${saved.provider_name} · NPI ${clean}` });
    } catch (e) { toast.error(describeError(e)); }
    finally { setVerifying(false); }
  };

  const pick = (list) => {
    let arr = Array.from(list || []).filter(Boolean);
    if (!arr.length) return;
    if (arr.length > 10) { toast.error("Up to 10 files at a time"); arr = arr.slice(0, 10); }
    for (const f of arr) { const err = validateDocFile(f); if (err) { toast.error(err); return; } }
    setDupNotice(null);
    setStaged(isSmart || m.multi ? arr : [arr[0]]);
  };

  const openExisting = (notice) => {
    const n = notice || dupNotice;
    if (!n) return;
    onClose?.();
    navigate(`/create-claims?claim=${n.noteId}${n.clientId ? `&client=${n.clientId}` : ""}`);
  };

  const commit = async () => {
    if (!staged || !canStart || committingRef.current) return;
    committingRef.current = true; setCommitting(true); setDupNotice(null);
    try {
      // Remittances are async in V2 — enqueue a batch and send the user to the tracker.
      if (what === "remittance" && staged.length > 1) {
        setEnqueuing(true);
        let ok = 0;
        for (const f of staged) { try { await uploadEob(f); ok++; } catch (e) { toast.error(`${f.name}: ${describeError(e)}`); } }
        setEnqueuing(false);
        if (ok) { toast.success(`${ok} remittance${ok > 1 ? "s" : ""} queued — track progress in Payments`); onClose?.(); navigate("/payments"); }
        return;
      }
      if (what === "remittance") { setEraFile(staged[0]); return; }
      // Claim → ONE smart multi-part path. Auto-splits by patient + DOS into N visits with an
      // error queue; the same path handles a single document (→ 1 visit) and a mixed bundle.
      const res = await startSplitUpload(staged, npi);
      if (res?.batch_id) setBatch({ id: res.batch_id, status: res.status || "processing" });
      else toast.error("Upload didn't start — please try again.");
    } catch (e) {
      toast.error(describeError(e));
    } finally {
      committingRef.current = false; setCommitting(false);
    }
  };

  const backToChooser = () => { setFiles(null); setEraFile(null); setBatch(null); setWide(false); setEraViewOpen(false); setSingleSettled(false); setStaged(null); };

  const done = () => { onDone?.(); onClose?.(); };

  return (
    <>
      {!minimized ? <div className="lu-backdrop" onClick={() => { if (!inFlow) onClose?.(); }} data-testid="lens-backdrop" /> : null}
      <div className={`lu-modal ${wide || eraViewOpen ? "lu-wide" : ""}`} role="dialog" data-testid="lens-upload-modal"
        style={minimized ? { display: "none" } : undefined}>
        {/* HEADER */}
        <div className="lu-head">
          <div className="lu-head-ic"><ScanLine size={18} /></div>
          <div className="lu-head-txt">
            <div className="lu-head-title">Upload</div>
            <div className="lu-head-sub">
              {inFlow ? (batch ? "Multi-part upload · smart split" : `${cfg.label} · ${m.title}`) : "Lens reads what you send and codes it."}
            </div>
          </div>
          {inFlow ? (
            <button className="lu-back" onClick={backToChooser} data-testid="lens-back">Back</button>
          ) : null}
          <button className="lu-x" onClick={closeModal} data-testid="lens-close" aria-label="Close" title={queuedJob ? "Runs in the background" : "Close"}><X size={16} /></button>
        </div>

        <div className="lu-body">
          {!inFlow ? (
            <div className="lu-chooser" data-testid="lens-chooser">
              {/* 1 · PROVIDER — claim uploads only */}
              {showProvider ? (
              <div className="lu-provider">
                <div className="lu-lbl"><span className="lu-num">1</span>Billing provider</div>
                <div className="lu-prov-row">
                  {loadingP ? (
                    <div className="lu-loading"><Loader2 size={14} className="lu-spin" /> Loading your providers…</div>
                  ) : providers.length ? (
                    <select value={npi} onChange={(e) => setNpi(e.target.value)} data-testid="lens-provider-select" className="lu-select">
                      {providers.map((p) => <option key={p.npi_number} value={p.npi_number}>{`${p.provider_name} · NPI ${p.npi_number}`}</option>)}
                    </select>
                  ) : <div className="lu-muted" data-testid="lens-no-client-providers">No providers linked to Client {selectedClientId} yet — add one.</div>}
                  {!showAddNpi ? (
                    <button className="lu-addbtn" onClick={() => setShowAddNpi(true)} data-testid="lens-add-provider">Add a provider</button>
                  ) : null}
                </div>
                {showAddNpi ? (
                  <div className="lu-npi-row">
                    <input value={addNpi} onChange={(e) => setAddNpi(e.target.value)} placeholder="Provider NPI…" inputMode="numeric" maxLength={10}
                      data-testid="lens-npi-input" className="lu-input" autoFocus />
                    <button onClick={verify} disabled={verifying} data-testid="lens-npi-verify" className="lu-verify">
                      {verifying ? <Loader2 size={13} className="lu-spin" /> : <CheckCircle2 size={13} />} Look up
                    </button>
                  </div>
                ) : null}
              </div>
              ) : null}

              {/* 2 · WHAT — two cards, the card is the control (only when both flows are offered) */}
              {showWhatChooser ? (
              <>
              <div className="lu-lbl"><span className="lu-num">2</span>What are you uploading</div>
              <div className="lu-kinds" data-testid="lens-what">
                <button className={`lu-kind ${what === "claim" ? "on" : ""}`} onClick={() => setWhat("claim")} data-testid="lens-what-claim">
                  <span className="lu-kind-ic"><FileText size={17} /></span>
                  <span className="lu-kind-t">Claim documents</span>
                  <span className="lu-kind-d">Superbills &amp; notes → claims</span>
                </button>
                {hasEra ? (
                  <button className={`lu-kind ${what === "remittance" ? "on" : ""}`} onClick={() => setWhat("remittance")} data-testid="lens-what-remittance">
                    <span className="lu-kind-ic"><CreditCard size={17} /></span>
                    <span className="lu-kind-t">Remittances</span>
                    <span className="lu-kind-d">Paper EOBs → an ERA</span>
                  </button>
                ) : null}
              </div>
              </>
              ) : null}

              {/* 3 · (claims use one smart path — no grouping choice) */}

              {/* DROP ZONE */}
              <div
                className={`lu-drop ${canStart ? "" : "lu-drop-off"} ${drag ? "lu-drop-drag" : ""}`}
                onClick={() => canStart && !staged && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
                onDrop={(e) => { e.preventDefault(); setDrag(false); if (canStart) pick(e.dataTransfer?.files); }}
                data-testid="lens-dropzone"
              >
                <input ref={inputRef} type="file" accept={ACCEPTED} multiple={isSmart || m.multi} className="lu-hidden"
                  onChange={(e) => { pick(e.target.files); e.target.value = ""; }} data-testid="lens-file-input" />
                {staged ? (
                  <div className="lu-staged" data-testid="lens-staged">
                    <FileText size={18} />
                    <div className="lu-staged-txt">
                      <div className="lu-staged-n">{staged.length === 1 ? staged[0].name : `${staged.length} files ready`}</div>
                      <div className="lu-staged-s">Ready — or choose another</div>
                    </div>
                    <button className="lu-staged-x" onClick={(e) => { e.stopPropagation(); setStaged(null); inputRef.current?.click(); }} data-testid="lens-restage">Change</button>
                  </div>
                ) : (
                  <>
                    <div className="lu-drop-ic"><UploadCloud size={22} /></div>
                    <div className="lu-drop-t">{(what === "claim" ? "Drop your document(s)" : m.dropTitle.replace(", or browse", ""))}, or <span className="lu-browse">browse</span></div>
                    <div className="lu-types">{what === "remittance" ? "EOB · remittance · PDF · photo" : "Superbill · note · PDF · photo — many patients & dates split automatically"}</div>
                    <div className="lu-drop-s">PDF, PNG or JPG · up to 50 MB each{(isSmart || m.multi) ? " · up to 10 files" : ""}</div>
                  </>
                )}
              </div>

              {/* INSURANCE / ID CARD — visit packet only (payer & member ID read into this claim) */}
              {mode === "visit" ? (
                <div className="lu-card-panel" data-testid="lens-card-panel">
                  <div className="lu-card-ic"><IdCard size={16} /></div>
                  <div className="lu-card-txt">
                    <div className="lu-card-t">Add a card to this upload</div>
                    <div className="lu-card-sub">Its payer &amp; member ID are read into this claim</div>
                  </div>
                  <button className="lu-card-btn" onClick={() => cardRef.current?.click()} data-testid="lens-add-card">Add card</button>
                  <input ref={cardRef} type="file" accept={ACCEPTED} className="lu-hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]; e.target.value = "";
                      if (!f) return;
                      const err = validateDocFile(f); if (err) { toast.error(err); return; }
                      setStaged((prev) => [...(prev || []), f]);
                      toast.success("Card added to the visit packet");
                    }} data-testid="lens-card-input" />
                </div>
              ) : null}

              {/* DUPLICATE NOTICE — these exact files + provider were already uploaded */}
              {dupNotice ? (
                <div className="lu-dup" data-testid="lens-dup-notice" style={{ margin: "12px 0 0", padding: "12px 14px", borderRadius: 12, border: "1px solid rgba(255,159,10,.4)", background: "rgba(255,159,10,.1)" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)" }}>Already uploaded</div>
                  <div style={{ fontSize: 11.5, color: "var(--gray2)", marginTop: 2, lineHeight: 1.5 }}>
                    These exact file{stagedN > 1 ? "s" : ""} and this provider were sent before, so revQ didn’t create a new claim.
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <button className="lu-primary" onClick={() => openExisting()} data-testid="lens-dup-open">Open the existing claim</button>
                    <button onClick={() => setDupNotice(null)} data-testid="lens-dup-dismiss"
                      style={{ fontSize: 12, fontWeight: 600, color: "var(--gray1)", padding: "8px 10px", borderRadius: 10, background: "transparent", border: "none", cursor: "pointer" }}>Dismiss</button>
                  </div>
                </div>
              ) : null}

              {/* FOOTER — sample left, primary right (names the action) */}
              <div className="lu-foot">
                <button className="lu-foot-sample" onClick={() => toast.message(`${m.sample} — sample corpus coming soon`)} data-testid="lens-sample">Try a sample document</button>
                <span className="lu-foot-sp" />
                <button className="lu-primary" disabled={!staged || !canStart || enqueuing || committing} onClick={commit} data-testid="lens-commit">{enqueuing || committing ? "Working…" : primaryLabel}</button>
              </div>
            </div>
          ) : (
            <div className="lu-flow" data-testid="lens-flow">
              {batch ? (
                <SplitBatchTracker
                  batchId={batch.id}
                  clientId={selectedClientId}
                  onOpenVisit={(noteId) => { onClose?.(); navigate(`/create-claims?claim=${encodeURIComponent(noteId)}${selectedClientId ? `&client=${encodeURIComponent(selectedClientId)}` : ""}`); }}
                  onDone={done}
                />
              ) : eraFile ? (
                <EobEraFlow
                  file={eraFile}
                  autoLink={{ clientId: selectedClientId || firstClientId }}
                  eraViewOpen={eraViewOpen}
                  onEraViewChange={setEraViewOpen}
                  onBack={backToChooser}
                  onChooseFlow={backToChooser}
                  onFinish={done}
                />
              ) : mode === "visit" ? (
                <NoteClaimFlow
                  visitFiles={files}
                  npi={npi}
                  clientId={selectedClientId}
                  fingerprint={fp}
                  onBack={backToChooser}
                  onFinish={done}
                  onSettled={setSingleSettled}
                  onQueued={setQueuedJob}
                  onWatch={handleWatch}
                  onBackground={handleBackground}
                />
              ) : files && files.length === 1 ? (
                <NoteClaimFlow
                  file={files[0]}
                  npi={npi}
                  clientId={selectedClientId}
                  fingerprint={fp}
                  onBack={backToChooser}
                  onFinish={done}
                  onSettled={setSingleSettled}
                  onQueued={setQueuedJob}
                  onWatch={handleWatch}
                  onBackground={handleBackground}
                />
              ) : (
                <MultiClaimFlow files={files} npi={npi} clientId={selectedClientId} onBack={backToChooser} onFinish={done} onProgress={(d) => setMultiDone(d)} />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
